import IntegratedPersonPhotoInput, { isIntegratedPersonPhoto } from '../components/IntegratedPersonPhotoInput';
import IntegratedAcademicSelect, { isIntegratedAcademicField } from '../components/IntegratedAcademicSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../AuthContext";
import api from "../api";
import FileAttachment from "../components/FileAttachment";
import DecimalInput from "../components/DecimalInput";
import IntegerInput from "../components/IntegerInput";
import MaskedInput from "../components/MaskedInput";
import { createRequestPdf } from "../utils/requestPdfExport";
import { hasMultipleResponsibleTargets } from "../utils/responsibles";
import DynamicSelectionField from "../components/DynamicSelectionField";
import { applyAttachmentTemplate } from "../utils/attachmentNaming";
import StepHistory from "../components/StepHistory";
import TableField from "../components/TableField";
import { FaPenNib, FaHistory, FaFilePdf } from "react-icons/fa";
import {
  getMaxUploadFileSizeMessage,
  getUploadLimitExceededMessage,
  isFileWithinUploadLimit,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_MB,
} from "../utils/uploadLimits";
import { emitDashboardRefresh } from "../utils/dashboardEvents";
import { generateDocumentPdf } from "../services/documentPdfService";
import usePendingFileUploads from "../hooks/usePendingFileUploads";
import { evaluateVisibilityExpression } from "../utils/safeExpression";
import { getCopyableInitialValues } from "../utils/processCopy";



const getAutoFillValue = (source, user) => {
  if (!source) return "";
  const today = new Date().toISOString().slice(0, 10);
  const sourceMap = {
    user_name: user?.name || "",
    today_date: today,
    user_email: user?.email || "",
    user_cpf: user?.cpf || "",
    user_birth_date: user?.birthDate ? String(user.birthDate).slice(0, 10) : "",
    user_sector: user?.sector?.name || "",
  };
  return sourceMap[source] ?? "";
};

const applyAutoFillDefaults = (fields = [], values = {}, user) => {
  const nextValues = { ...values };
  fields.forEach((field) => {
    if (!field?.id || !field.autoFillSource) return;
    const currentValue = nextValues[field.id];
    const isEmpty =
      currentValue === undefined ||
      currentValue === null ||
      currentValue === "";
    if (!isEmpty) return;
    const prefilled = getAutoFillValue(field.autoFillSource, user);
    if (prefilled !== "") {
      nextValues[field.id] = prefilled;
    }
  });
  return nextValues;
};



const normalizeLinkedMappedValue = (rawValue, targetField = null, seen = new Set()) => {
  if (rawValue === null || rawValue === undefined) return "";

  if (typeof rawValue === "string") {
    const trimmed = rawValue.trim();
    const linkedFieldMatch = trimmed.match(/^\d+:[^:]+:([\s\S]*)$/);
    return linkedFieldMatch ? linkedFieldMatch[1].trim() : rawValue;
  }

  if (typeof rawValue === "number" || typeof rawValue === "boolean") {
    return rawValue;
  }

  if (typeof rawValue !== "object") {
    return String(rawValue);
  }

  if (seen.has(rawValue)) return "";
  seen.add(rawValue);

  const targetType = String(targetField?.type || "").trim().toLowerCase();
  const targetDescriptor = [targetType, targetField?.id, targetField?.label]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const wantsEmail = /e-?mail/.test(targetDescriptor);
  const wantsRawValue = ["number", "integer", "decimal", "currency", "checkbox", "date", "datetime"].includes(targetType);

  if (Array.isArray(rawValue)) {
    const entries = rawValue
      .map((entry) => normalizeLinkedMappedValue(entry, targetField, seen))
      .filter((entry) => entry !== "" && entry !== null && entry !== undefined);
    return entries.length <= 1 ? (entries[0] ?? "") : entries.join(", ");
  }

  const preferredKeys = wantsEmail
    ? ["email", "emailAddress", "displayValue", "label", "value", "text", "name", "address"]
    : wantsRawValue
      ? ["value", "amount", "number", "displayValue", "label", "text", "name"]
      : ["displayValue", "label", "text", "name", "email", "emailAddress", "value"];

  let fallback = "";
  for (const key of preferredKeys) {
    if (!Object.prototype.hasOwnProperty.call(rawValue, key)) continue;
    const candidate = normalizeLinkedMappedValue(rawValue[key], targetField, seen);
    if (candidate === "" || candidate === null || candidate === undefined) continue;
    if (wantsEmail && typeof candidate === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate.trim())) {
      return candidate.trim();
    }
    if (fallback === "") fallback = candidate;
  }

  if (wantsEmail) {
    for (const nestedValue of Object.values(rawValue)) {
      const candidate = normalizeLinkedMappedValue(nestedValue, targetField, seen);
      if (typeof candidate === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate.trim())) {
        return candidate.trim();
      }
    }
  }

  if (fallback !== "") return fallback;

  for (const nestedValue of Object.values(rawValue)) {
    const candidate = normalizeLinkedMappedValue(nestedValue, targetField, seen);
    if (candidate !== "" && candidate !== null && candidate !== undefined) {
      return candidate;
    }
  }

  return "";
};

function fieldVisible(field, values) {
  return evaluateVisibilityExpression(field?.visibleWhen, values);
}

export default function ProcessRunner({
  processId,
  instanceId,
  onStart,
  onStartError,
  onFinish,
  closeTab,
  activateTab,
  returnToTabId = 'requests',
  initialValues = null,
}) {
  const { user } = useAuth();
  // Session refreshes on focus must not start a second process instance.
  const userRef = useRef(user);
  const initialValuesRef = useRef(initialValues);
  userRef.current = user;
  initialValuesRef.current = initialValues;
  const onStartRef = useRef(onStart);
  const onStartErrorRef = useRef(onStartError);
  const [instance, setInstance] = useState(null);
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [pendingEvents, setPendingEvents] = useState([]);
  const [triggeringEventId, setTriggeringEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [linkedFieldOptions, setLinkedFieldOptions] = useState({});
  const [inventoryIntegratedOptions, setInventoryIntegratedOptions] = useState({ categories: [], warehouses: [] });

  useEffect(() => {
    onStartRef.current = onStart;
  }, [onStart]);

  useEffect(() => {
    onStartErrorRef.current = onStartError;
  }, [onStartError]);

  const [claimingTask, setClaimingTask] = useState(false);
  const [reassigningTask, setReassigningTask] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatingDocumentFieldId, setGeneratingDocumentFieldId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [aiAnswerModal, setAiAnswerModal] = useState(null);
  const [aiAnswerMode, setAiAnswerMode] = useState("fresh");
  const [aiAnswerLoading, setAiAnswerLoading] = useState(false);
  const [aiAnswerPrompt, setAiAnswerPrompt] = useState("");
  const [aiAnswerSettings, setAiAnswerSettings] = useState({
    loaded: false,
    configured: false,
  });
  const {
    pendingUploadsRef,
    pendingUploads,
    pendingUploadsCount,
    hasPendingUploads,
    startUpload,
    updateUpload,
    finishUpload,
    getUpload,
    getTableRowUploadCount,
    getTableUploadCount,
  } = usePendingFileUploads();
  const currentResponsibleEntry = useMemo(() => {
    if (!instance) return null;
    const stepKey = String(instance.currentStep ?? 0);
    const fromInstance = instance.stepResponsibles?.[stepKey];
    const fromProcess = instance.process?.stepResponsibles?.[stepKey];
    return fromInstance || fromProcess || null;
  }, [instance]);


  useEffect(() => {
    let active = true;
    const loadAiAnswerSettings = async () => {
      try {
        const { data } = await api.get("/ai/forms/settings");
        if (!active) return;
        setAiAnswerSettings({
          loaded: true,
          configured: Boolean(data?.formOpenAiApiKeyConfigured),
        });
      } catch (_) {
        if (!active) return;
        setAiAnswerSettings({ loaded: true, configured: false });
      }
    };
    loadAiAnswerSettings();
    return () => {
      active = false;
    };
  }, []);

  const mustClaimBeforeSubmit = useMemo(() => {
    if (!instance) return false;
    if (instance.currentAssignee && !instance.isCurrentAssignee) return true;
    if (instance.canClaim && !instance.isCurrentAssignee) return true;
    if (!instance.currentAssignee && !instance.canClaim) {
      return hasMultipleResponsibleTargets(currentResponsibleEntry);
    }
    return false;
  }, [instance, currentResponsibleEntry]);

  const currentStepName = useMemo(() => {
    if (!instance) return null;
    const names = instance.process?.stepNames;
    const currentIndex = instance.currentStep ?? 0;
    if (!names) return null;
    if (Array.isArray(names)) {
      const value = names[currentIndex];
      return typeof value === "string" && value.trim() ? value.trim() : null;
    }
    if (typeof names === "object" && names !== null) {
      const key = String(currentIndex);
      const value = names[key];
      return typeof value === "string" && value.trim() ? value.trim() : null;
    }
    return null;
  }, [instance]);

  const baseInputClasses = useMemo(
    () =>
      "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-200",
    [],
  );
  const readOnlyInputClasses =
    "bg-slate-100 text-slate-500 border-slate-200 shadow-none focus:border-slate-300 focus:ring-0";
  const getInputClassName = (isReadOnlyOrDisabled = false) =>
    `${baseInputClasses}${isReadOnlyOrDisabled ? ` ${readOnlyInputClasses}` : ""}`;

  const getEffectiveFieldOptions = (field) => {
    if (field?.moduleBinding?.moduleKey === "inventory") {
      if (field.moduleBinding.moduleField === "categoryId") return inventoryIntegratedOptions.categories;
      if (field.moduleBinding.moduleField === "warehouseId") return inventoryIntegratedOptions.warehouses;
    }
    return field.options || [];
  };

  const renderFieldControl = (
    field,
    {
      readOnly = false,
      allowSelectionWhenReadOnly = false,
    } = {},
  ) => {
    const value = values[field.id];
    const normalizedValue = value ?? "";
    const fieldLockedByAutoFill = Boolean(field.autoFillSource) && field.autoFillEditable === false;
    const isReadOnlyField = readOnly || fieldLockedByAutoFill;

    if (isIntegratedAcademicField(field)) {
      return <IntegratedAcademicSelect field={field} fields={(form?.schema?.fields || [])} values={values} disabled={isReadOnlyField} className={getInputClassName(isReadOnlyField)} onChange={patch => setValues(prev => { const next = { ...prev, ...patch }; if (instance) saveDraft(instance.id, instance.currentStep, next); return next; })} />;
    }
    if (field.type === "list") {
      const listValue = field.allowMultiple
        ? Array.isArray(value)
          ? value
          : value
            ? [value]
            : []
        : normalizedValue;

      return (
        <select
          id={field.id}
          name={field.id}
          value={listValue}
          onChange={handleChange}
          disabled={isReadOnlyField}
          required={field.required}
          className={`${getInputClassName(isReadOnlyField)} appearance-none`}
          multiple={field.allowMultiple}
        >
          {!field.allowMultiple && (
            <option value="" disabled>
              Selecione...
            </option>
          )}
          {getEffectiveFieldOptions(field).map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }
    if (field.type === "linkedField") {
      const options = Array.isArray(linkedFieldOptions[field.id]) ? linkedFieldOptions[field.id] : [];
      const listValue = field.multiple
        ? Array.isArray(value) ? value : value ? [value] : []
        : (value ?? "");
      return (
        <select
          id={field.id}
          name={field.id}
          value={listValue}
          onChange={(event) => {
            const selectedValue = field.multiple
              ? Array.from(event.target.selectedOptions).map((option) => option.value)
              : event.target.value;
            setValues((prev) => {
              const updated = { ...prev, [field.id]: selectedValue };
              if (!field.multiple && selectedValue) {
                const selectedOption = options.find((option) => String(option.key) === String(selectedValue));
                (Array.isArray(field.linkedFieldMappings) ? field.linkedFieldMappings : []).forEach((mapping) => {
                  const sourceFieldId = mapping?.sourceFieldId;
                  const targetFieldId = mapping?.targetFieldId;
                  if (!sourceFieldId || !targetFieldId || !selectedOption?.relatedValues) return;
                  if (Object.prototype.hasOwnProperty.call(selectedOption.relatedValues, sourceFieldId)) {
                    const targetField = Array.isArray(form?.schema?.fields)
                      ? form.schema.fields.find((candidate) => String(candidate?.id) === String(targetFieldId))
                      : null;
                    updated[targetFieldId] = normalizeLinkedMappedValue(
                      selectedOption.relatedValues[sourceFieldId],
                      targetField,
                    );
                  }
                });
              }
              if (instance) saveDraft(instance.id, instance.currentStep, updated);
              return updated;
            });
          }}
          disabled={isReadOnlyField}
          required={field.required}
          className={`${getInputClassName(isReadOnlyField)} appearance-none`}
          multiple={Boolean(field.multiple)}
        >
          {!field.multiple && <option value="">Selecione...</option>}
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.displayValue} {option.requestId ? `(#${option.requestId})` : ""}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "table") {
      return (
        <TableField
          field={field}
          value={value}
          onChange={(nextRows) => {
            setValues((prev) => {
              const updated = { ...prev, [field.id]: nextRows };
              if (instance) saveDraft(instance.id, instance.currentStep, updated);
              return updated;
            });
          }}
          readOnly={isReadOnlyField}
          inputClassName={getInputClassName(isReadOnlyField)}
          onUploadFile={isReadOnlyField ? undefined : ({ rowIndex, column, file }) => handleTableFileChange(field, rowIndex, column, file)}
          getCellUpload={(rowIndex, columnId) => getUpload(`table:${field.id}:${rowIndex}:${columnId}`)}
          getRowPendingUploadCount={(rowIndex) => getTableRowUploadCount(field.id, rowIndex)}
          getTablePendingUploadCount={() => getTableUploadCount(field.id)}
        />
      );
    }

    if (field.type === "dynamic-select") {
      return (
        <>
          <DynamicSelectionField
            value={value}
            onChange={(nextValue) =>
              handleDynamicSelectionChange(field.id, nextValue)
            }
            readOnly={isReadOnlyField}
            allowSelectionWhenReadOnly={allowSelectionWhenReadOnly}
            allowMultipleDefault={Boolean(field.allowMultiple)}
            required={field.required}
          />
          {field.lockedForStep && (
            <p className="text-xs text-indigo-600">
              Este campo está bloqueado para edição nesta etapa.
            </p>
          )}
        </>
      );
    }

    if (field.type === "document") {
      const isGeneratingThisDocument = generatingDocumentFieldId === field.id;
      return (
        <div className="inline-flex min-w-[260px] flex-col gap-2">
          <button
            type="button"
            onClick={() => handleGenerateDocumentPdf(field)}
            disabled={Boolean(generatingDocumentFieldId)}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 disabled:cursor-wait disabled:opacity-70"
          >
            <FaFilePdf /> {isGeneratingThisDocument ? "Gerando PDF..." : `Gerar PDF: ${field.documentTemplate?.title || field.label}`}
          </button>
          {isGeneratingThisDocument && (
            <div className="h-2 overflow-hidden rounded-full bg-indigo-100" role="progressbar" aria-label="Gerando documento PDF">
              <div className="asset-loading-bar h-full rounded-full bg-indigo-600" />
            </div>
          )}
        </div>
      );
    }

    if (isIntegratedPersonPhoto(field)) {
      return <IntegratedPersonPhotoInput field={field} value={values[field.id]} readOnly={readOnly} onChange={photo => setValues(previous => ({ ...previous, [field.id]: photo }))} />;
    }
    if (field.type === "file") {
      if (readOnly) {
        return <FileAttachment file={value} instanceId={instance?.id} />;
      }
      const fieldUpload = getUpload(`field:${field.id}`);
      return (
        <div className="space-y-2">
          {value && <FileAttachment file={value} instanceId={instance?.id} />}
          <input
            type="file"
            id={field.id}
            onChange={(event) => {
              const selectedFile = event.target.files?.[0];
              if (!selectedFile) return;
              if (selectedFile.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
                alert(getMaxUploadFileSizeMessage());
                event.target.value = "";
                return;
              }
              handleFileChange(field.id, selectedFile);
            }}
            required={field.required}
            disabled={Boolean(fieldUpload)}
            className="w-full cursor-pointer rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 file:mr-4 file:cursor-pointer file:rounded-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-indigo-600 hover:file:bg-indigo-100"
          />
          {fieldUpload && (
            <p className="text-xs text-blue-600">
              {fieldUpload.fileName} — {fieldUpload.status === "processing" ? "Processando arquivo..." : `${fieldUpload.percent || 0}%`}
            </p>
          )}
          <p className="text-xs text-slate-500">
            Tamanho máximo do arquivo: {MAX_UPLOAD_FILE_SIZE_MB} MB.
          </p>
        </div>
      );
    }

    if (field.type === "decimal") {
      return (
        <DecimalInput
          decimalPlaces={field.decimalPlaces || 0}
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "number") {
      return (
        <IntegerInput
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "phone") {
      return (
        <MaskedInput
          mask="phone"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "currency") {
      return (
        <MaskedInput
          mask="currency"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "cpf" || field.mask === "cpf") {
      return (
        <MaskedInput
          mask="cpf"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "cnpj") {
      return (
        <MaskedInput
          mask="cnpj"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "cep") {
      return (
        <MaskedInput
          mask="cep"
          name={field.id}
          id={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "email") {
      return (
        <div className="space-y-1.5">
          <input
            type="email"
            id={field.id}
            name={field.id}
            multiple
            value={normalizedValue}
            onChange={handleChange}
            readOnly={isReadOnlyField}
            required={field.required}
            inputMode="email"
            autoComplete="email"
            className={getInputClassName(isReadOnlyField)}
          />
          {!isReadOnlyField && (
            <p className="text-xs text-slate-500">
              Para mais de um destinatário, separe os e-mails por vírgula.
            </p>
          )}
        </div>
      );
    }

    if (field.type === "date") {
      return (
        <input
          type="date"
          id={field.id}
          name={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          onFocus={(event) => event.target.showPicker?.()}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "time") {
      return (
        <input
          type="time"
          id={field.id}
          name={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          className={getInputClassName(isReadOnlyField)}
        />
      );
    }

    if (field.type === "textarea") {
      const textareaRows = Math.max(3, Number(field.rows) || 0);
      return (
        <textarea
          id={field.id}
          name={field.id}
          value={normalizedValue}
          onChange={handleChange}
          readOnly={isReadOnlyField}
          required={field.required}
          rows={textareaRows}
          className={`${getInputClassName(isReadOnlyField)} resize-y`}
        />
      );
    }

    const inputType = field.type || "text";
    return (
      <input
        type={inputType}
        id={field.id}
        name={field.id}
        value={normalizedValue}
        onChange={handleChange}
        readOnly={readOnly}
        required={field.required}
        className={getInputClassName(readOnly)}
      />
    );
  };

  const draftKey = useCallback((instId, step) => {
    const userPart = user?.id ? `user-${user.id}` : "anon";
    const stepPart = Number.isInteger(step) ? `step-${step}` : "step-unknown";
    return `draft-${userPart}-${instId}-${stepPart}`;
  }, [user?.id]);

  const loadDraft = useCallback((instId, step) => {
    try {
      const d = localStorage.getItem(draftKey(instId, step));
      return d ? JSON.parse(d) : {};
    } catch {
      return {};
    }
  }, [draftKey]);

  const saveDraft = useCallback((instId, step, vals) => {
    try {
      localStorage.setItem(draftKey(instId, step), JSON.stringify(vals));
    } catch {}
  }, [draftKey]);

  const clearDraft = (instId) => {
    try {
      const userPart = user?.id ? `user-${user.id}` : "anon";
      const prefix = `draft-${userPart}-${instId}-`;
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {}
  };


  const stringifyFormValue = (value) => {
    if (value === undefined || value === null) return "";
    if (Array.isArray(value)) return value.map(stringifyFormValue).filter(Boolean).join(", ");
    if (typeof value === "object") {
      if (value.name) return value.name;
      if (value.displayValue) return value.displayValue;
      if (Array.isArray(value.selected)) return value.selected.map(stringifyFormValue).filter(Boolean).join(", ");
      try {
        return JSON.stringify(value);
      } catch {
        return "";
      }
    }
    return String(value);
  };

  const canUseAiAnswer = (field, readOnly = false) => {
    if (!field?.id || readOnly || field.readOnly || field.allowAiAnswer !== true) return false;
    return ["text", "textarea", "email", "number", "decimal", "currency"].includes(field.type || "text");
  };

  const openAiAnswerModal = async (field) => {
    let configured = aiAnswerSettings.configured;
    if (!aiAnswerSettings.loaded) {
      try {
        const { data } = await api.get("/ai/forms/settings");
        configured = Boolean(data?.formOpenAiApiKeyConfigured);
        setAiAnswerSettings({ loaded: true, configured });
      } catch (_) {
        configured = false;
        setAiAnswerSettings({ loaded: true, configured: false });
      }
    }
    if (!configured) {
      alert("A ferramenta não possui integração ativa de inteligência artificial.");
      return;
    }
    const currentValue = stringifyFormValue(values[field.id]).trim();
    setAiAnswerMode(currentValue ? "rewrite" : "fresh");
    setAiAnswerPrompt("");
    setAiAnswerModal(field);
  };

  const handleGenerateAiAnswer = async () => {
    if (!aiAnswerModal || aiAnswerLoading) return;
    setAiAnswerLoading(true);
    try {
      const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
      const payload = {
        mode: aiAnswerMode,
        formName: form?.name || instance?.process?.name || "Formulário",
        processName: instance?.process?.name || "",
        requestTitle: instance?.id
          ? `Solicitação ${instance.id} - ${instance?.process?.name || form?.name || "Formulário"}`
          : instance?.process?.name || form?.name || "Formulário",
        prompt: aiAnswerPrompt,
        targetField: {
          id: aiAnswerModal.id,
          label: aiAnswerModal.label,
          type: aiAnswerModal.type,
          value: values[aiAnswerModal.id],
        },
        fields: schemaFields
          .filter((field) => fieldVisible(field, values))
          .map((field) => ({
            id: field.id,
            label: field.label,
            type: field.type,
            value: field.id === aiAnswerModal.id && aiAnswerMode === "fresh" ? "" : values[field.id],
          })),
      };
      const { data } = await api.post("/ai/forms/answer", payload);
      const answer = data?.answer || "";
      setValues((prev) => {
        const updated = { ...prev, [aiAnswerModal.id]: answer };
        if (instance) saveDraft(instance.id, instance.currentStep, updated);
        return updated;
      });
      setAiAnswerModal(null);
    } catch (err) {
      alert(err.response?.data?.error || "Não foi possível gerar a resposta com IA.");
    } finally {
      setAiAnswerLoading(false);
    }
  };

  const handleClaimTask = async () => {
    if (!instance) return;
    setClaimingTask(true);
    try {
      const res = await api.post(`/processes/instances/${instance.id}/claim`);
      setInstance(res.data);
    } catch (err) {
      const message = err.response?.data?.error || "Erro ao assumir tarefa.";
      alert(message);
    } finally {
      setClaimingTask(false);
    }
  };

  const handleReleaseTask = async () => {
    if (!instance) return;
    setReassigningTask(true);
    try {
      const res = await api.post(`/processes/instances/${instance.id}/reassign`, {
        userId: null,
      });
      setInstance(res.data);
    } catch (err) {
      const message = err.response?.data?.error || "Erro ao liberar tarefa.";
      alert(message);
    } finally {
      setReassigningTask(false);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const fetchData = async () => {
      try {
        let res;
        if (instanceId) {
          res = await api.get(`/processes/instances/${instanceId}/form`);
        } else if (processId) {
          res = await api.post(`/processes/${processId}/start-form`);
        }
        if (!res) {
          if (active) setLoading(false);
          return;
        }
        const inst = res.data.instance;
        let form = res.data.form;
        const events = Array.isArray(res.data.waitingEvents)
          ? res.data.waitingEvents
          : [];
        if (!form && inst.process?.formId) {
          try {
            const fRes = await api.get(`/forms/${inst.process.formId}`);
            form = fRes.data;
          } catch {}
        }
        if (!active) return;
        setPendingEvents(events);
        setInstance(inst);
        setForm(form);
        const draft = form ? loadDraft(inst.id, inst.currentStep) : {};
        const saved = inst.formData || {};
        const isExistingInstance = Boolean(instanceId);
        if (form) {
          const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
          const currentInitialValues = initialValuesRef.current;
          const providedInitialValues =
            !isExistingInstance && currentInitialValues && typeof currentInitialValues === "object" && !Array.isArray(currentInitialValues)
              ? getCopyableInitialValues(schemaFields, currentInitialValues)
              : {};
          const baseValues = isExistingInstance
            ? { ...saved, ...(Object.keys(draft).length ? draft : {}) }
            : providedInitialValues;
          setValues(applyAutoFillDefaults(schemaFields, baseValues, userRef.current));
        } else {
          setValues(isExistingInstance ? saved : {});
        }
        if (onStartRef.current) onStartRef.current(inst);
      } catch (err) {
        if (!active) return;
        const message =
          err.response?.data?.error || "Erro ao carregar formulário";
        setError(message);
        if (onStartErrorRef.current) onStartErrorRef.current(message);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [processId, instanceId, loadDraft]);

  useEffect(() => {
    setShowHistory(false);
  }, [instance?.id]);

  useEffect(() => {
    const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
    const linkedFields = schemaFields.filter((field) => field?.type === "linkedField" && field?.linkedFieldId);
    if (!linkedFields.length) {
      setLinkedFieldOptions({});
      return;
    }
    let active = true;
    Promise.all(
      linkedFields.map(async (field) => {
        const { data } = await api.get("/forms/linked-field/options", {
          params: {
            linkedProcessId: field.linkedProcessId || undefined,
            linkedFormId: field.linkedFormId || undefined,
            linkedFieldId: field.linkedFieldId,
            sourceStatus: field.sourceStatus || "completed",
            includeFieldIds: Array.from(new Set(
              (Array.isArray(field.linkedFieldMappings) ? field.linkedFieldMappings : [])
                .map((mapping) => mapping?.sourceFieldId)
                .filter(Boolean)
            )).join(",") || undefined,
          },
        });
        return [field.id, data?.options || []];
      })
    )
      .then((entries) => {
        if (!active) return;
        setLinkedFieldOptions(Object.fromEntries(entries));
      })
      .catch(() => {
        if (!active) return;
        setLinkedFieldOptions({});
      });
    return () => {
      active = false;
    };
  }, [form]);

  useEffect(() => {
    const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
    const needsInventoryOptions = schemaFields.some((field) => {
      const binding = field?.moduleBinding;
      return binding?.moduleKey === "inventory" && ["categoryId", "warehouseId"].includes(binding?.moduleField);
    });
    if (!needsInventoryOptions) {
      setInventoryIntegratedOptions({ categories: [], warehouses: [] });
      return;
    }
    let active = true;
    Promise.all([
      api.get("/inventory/categories").catch(() => ({ data: [] })),
      api.get("/inventory/warehouses").catch(() => ({ data: [] })),
    ]).then(([categoriesRes, warehousesRes]) => {
      if (!active) return;
      const normalizeResponse = (response) => (Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : []);
      setInventoryIntegratedOptions({
        categories: normalizeResponse(categoriesRes).map((item) => `${item.id} - ${item.name}`),
        warehouses: normalizeResponse(warehousesRes).map((item) => `${item.id} - ${item.name}`),
      });
    });
    return () => {
      active = false;
    };
  }, [form]);

  useEffect(() => {
    return () => {
      if (instance) {
        saveDraft(instance.id, instance.currentStep, values);
      }
    };
  }, [instance, saveDraft, values]);

  useEffect(() => {
    if (!loading && instance && typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }, [loading, instance]);

  const fillAddressFromCep = async (field, cepValue) => {
    const cep = digitsOnly(cepValue);
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data?.erro) return;
      const addressValues = {
        'address.street': data.logradouro || '',
        'address.neighborhood': data.bairro || '',
        'address.city': data.localidade || '',
        'address.state': data.uf || '',
      };
      const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
      setValues((current) => {
        const updated = { ...current };
        schemaFields.forEach((candidate) => {
          if (candidate.groupId !== field.groupId) return;
          const moduleField = candidate.moduleBinding?.moduleField;
          if (moduleField && addressValues[moduleField] !== undefined && !updated[candidate.id]) {
            updated[candidate.id] = addressValues[moduleField];
          }
        });
        if (instance) saveDraft(instance.id, instance.currentStep, updated);
        return updated;
      });
    } catch (_) {
      // CEP manual continua disponível se a busca externa falhar.
    }
  };

  const handleChange = (e) => {
    const { name, value, options, multiple, tagName } = e.target;
    const isMultipleSelect = multiple && String(tagName || "").toLowerCase() === "select";
    const nextValue = isMultipleSelect
      ? Array.from(options || [])
          .filter((option) => option.selected)
          .map((option) => option.value)
      : value;

    const changedField = form?.schema?.fields?.find((field) => field.id === name);
    setValues((v) => {
      const updated = { ...v, [name]: nextValue };
      if (instance) saveDraft(instance.id, instance.currentStep, updated);
      return updated;
    });
    if (changedField?.type === 'cep') {
      fillAddressFromCep(changedField, nextValue);
    }
  };

  const handleFileChange = async (name, file) => {
    if (!file) return;
    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      return;
    }
    const field = form?.schema?.fields?.find((f) => f.id === name);
    const schemaFields = Array.isArray(form?.schema?.fields) ? form.schema.fields : [];
    let selectedFile = file;
    let generatedFileName = null;
    if (field?.fileNameTemplate) {
      const { file: renamedFile, generatedName } = applyAttachmentTemplate(file, field.fileNameTemplate, {
        values,
        fields: schemaFields,
        originalFileName: file.name,
        extras: {
          requestId: instance?.id,
          requestProtocol: instance?.protocol,
          solicitante:
            instance?.requester?.name ||
            instance?.user?.name ||
            instance?.startedBy?.name ||
            "",
        },
      });
      selectedFile = renamedFile;
      generatedFileName = generatedName;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    const category =
      instance?.process?.category || instance?.process?.name || "processo-sem-categoria";
    const formName = form?.name || instance?.process?.name || "Formulário";
    const fieldTitle = field?.label || name;
    formData.append("category", category);
    formData.append("formName", formName);
    formData.append("fieldTitle", fieldTitle);
    formData.append("insertedAt", new Date().toISOString());
    if (generatedFileName) {
      formData.append("generatedFileName", generatedFileName);
    }
    if (instance) {
      formData.append("requestId", String(instance.id));
      if (instance.protocol) {
        formData.append("requestProtocol", instance.protocol);
      }
      const requesterName =
        instance.requester?.name ||
        instance.user?.name ||
        instance.startedBy?.name ||
        "";
      if (requesterName) {
        formData.append("requesterName", requesterName);
      }
      const requesterSector =
        instance.requester?.sector?.name ||
        instance.user?.sector?.name ||
        instance.startedBy?.sector?.name ||
        "";
      if (requesterSector) {
        formData.append("requesterSector", requesterSector);
      }
    }
    const uploadKey = `field:${name}`;
    startUpload(uploadKey, selectedFile.name || file.name);
    try {
      const res = await api.post("/upload", formData, {
        onUploadProgress: (event) => {
          const percent = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
          updateUpload(uploadKey, {
            percent,
            status: percent >= 100 ? "processing" : "uploading",
          });
        },
      });
      const fileInfo = {
        url: res.data.url,
        downloadUrl: res.data.downloadUrl,
        downloadPath: res.data.downloadPath,
        relativePath: res.data.relativePath,
        name: res.data.name,
        metadata: res.data.metadata,
      };
      setValues((v) => {
        const updated = { ...v, [name]: fileInfo };
        if (instance) saveDraft(instance.id, instance.currentStep, updated);
        return updated;
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error ||
            error?.message ||
            "Erro ao enviar arquivo";
      alert(message);
    } finally {
      finishUpload(uploadKey);
    }
  };

  const handleTableFileChange = async (field, rowIndex, column, file) => {
    if (!file) return;
    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    const category =
      instance?.process?.category || instance?.process?.name || "processo-sem-categoria";
    const formName = form?.name || instance?.process?.name || "Formulário";
    formData.append("category", category);
    formData.append("formName", formName);
    formData.append("fieldTitle", `${field?.label || field?.id} - ${column?.label || column?.id}`);
    formData.append("insertedAt", new Date().toISOString());
    if (instance) {
      formData.append("requestId", String(instance.id));
      if (instance.protocol) formData.append("requestProtocol", instance.protocol);
      const requesterName = instance.requester?.name || instance.user?.name || instance.startedBy?.name || "";
      if (requesterName) formData.append("requesterName", requesterName);
      const requesterSector = instance.requester?.sector?.name || instance.user?.sector?.name || instance.startedBy?.sector?.name || "";
      if (requesterSector) formData.append("requesterSector", requesterSector);
    }

    const uploadKey = `table:${field.id}:${rowIndex}:${column.id}`;
    startUpload(uploadKey, file.name);
    try {
      const res = await api.post("/upload", formData, {
        onUploadProgress: (event) => {
          const percent = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
          updateUpload(uploadKey, {
            percent,
            status: percent >= 100 ? "processing" : "uploading",
          });
        },
      });
      const fileInfo = {
        url: res.data.url,
        downloadUrl: res.data.downloadUrl,
        downloadPath: res.data.downloadPath,
        relativePath: res.data.relativePath,
        name: res.data.name,
        metadata: res.data.metadata,
      };
      setValues((prev) => {
        const rows = Array.isArray(prev[field.id]) ? [...prev[field.id]] : [];
        const row = rows[rowIndex] && typeof rows[rowIndex] === "object" ? rows[rowIndex] : {};
        rows[rowIndex] = {
          ...row,
          [column.id]: fileInfo,
        };
        const updated = { ...prev, [field.id]: rows };
        if (instance) saveDraft(instance.id, instance.currentStep, updated);
        return updated;
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error ||
            error?.message ||
            "Erro ao enviar arquivo";
      alert(message);
    } finally {
      finishUpload(uploadKey);
    }
  };


  const handleGenerateDocumentPdf = async (field, { preview = false } = {}) => {
    setGeneratingDocumentFieldId(field.id);
    const openedPdfWindow = typeof window !== "undefined" ? window.open("", "_blank") : null;
    try {
      await generateDocumentPdf({
        field,
        fields: form?.schema?.fields || [],
        values,
        instance,
        preview,
        openedPdfWindow,
      });
    } catch (error) {
      alert(error?.message || "Não foi possível gerar o documento PDF.");
    } finally {
      setGeneratingDocumentFieldId(null);
    }
  };

  const handleDynamicSelectionChange = (fieldId, nextValue) => {
    setValues((prev) => {
      const updated = { ...prev, [fieldId]: nextValue };
      if (instance) saveDraft(instance.id, instance.currentStep, updated);
      return updated;
    });
  };

  const handleDownloadPdf = () => {
    if (!instance) return;
    const document = createRequestPdf({ instance, form, values });
    document.save(`solicitacao-${instance.id}.pdf`);
  };

  const handleTriggerEvent = async (eventInfo) => {
    if (!instance || !eventInfo?.id) return;
    try {
      setTriggeringEventId(eventInfo.id);
      await api.post(`/processes/instances/${instance.id}/events`, {
        eventId: eventInfo.id,
      });
      const res = await api.get(`/processes/instances/${instance.id}/form`);
      const inst = res.data.instance;
      const nextForm = res.data.form || null;
      const waiting = Array.isArray(res.data.waitingEvents)
        ? res.data.waitingEvents
        : [];
      setInstance(inst);
      setForm(nextForm);
      setPendingEvents(waiting);
      clearDraft(inst.id);
      const saved = inst?.formData || {};
      const schemaFields = Array.isArray(nextForm?.schema?.fields) ? nextForm.schema.fields : [];
      setValues(applyAutoFillDefaults(schemaFields, saved, user));
    } catch (err) {
      const message = err.response?.data?.error || "Erro ao registrar evento.";
      alert(message);
    } finally {
      setTriggeringEventId(null);
    }
  };

  const handleSubmit = async () => {
    if (!instance || submitting) return;
    setSubmitError(null);
    const pendingCount = pendingUploadsRef.current.size;
    if (pendingCount > 0) {
      setSubmitError(`Aguarde o término do envio de ${pendingCount} arquivo(s).`);
      return;
    }
    const reqFields = (form.schema?.fields || []).filter(
      (f) => f.required && !f.readOnly && fieldVisible(f, values),
    );
    for (const f of reqFields) {
      const v = values[f.id];
      if (f.type === "dynamic-select") {
        const optionCount = Array.isArray(v?.options) ? v.options.length : 0;
        if (optionCount === 0) {
          setSubmitError(`Adicione opções no campo "${f.label}" antes de prosseguir.`);
          return;
        }
        const selectedCount = Array.isArray(v?.selected) ? v.selected.length : 0;
        if (selectedCount === 0) {
          setSubmitError(`Selecione ao menos uma opção no campo "${f.label}".`);
          return;
        }
        continue;
      }
      if (f.type === "table") {
        const rows = Array.isArray(v) ? v : [];
        if (rows.length === 0) {
          setSubmitError(`Adicione ao menos uma linha no campo "${f.label}".`);
          return;
        }
        const requiredColumns = Array.isArray(f.tableColumns)
          ? f.tableColumns.filter((column) => column.required)
          : [];
        for (const column of requiredColumns) {
          const hasMissing = rows.some((row) => {
            const cell = row?.[column.id];
            return cell === undefined || cell === null || cell === "";
          });
          if (hasMissing) {
            setSubmitError(`Preencha a coluna obrigatória "${column.label}" em "${f.label}".`);
            return;
          }
        }
        continue;
      }
      if (v === undefined || v === "") {
        setSubmitError(`O campo "${f.label}" é obrigatório.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/processes/instances/${instance.id}/submit`,
        values,
        {
          headers: {
            "X-BPM-Expected-Step": String(instance.currentStep),
          },
        },
      );
      emitDashboardRefresh();
      const savedValues = res.data.formData || {};
      const step = instance.currentStep;
      const waiting = Array.isArray(res.data.waitingEvents)
        ? res.data.waitingEvents
        : [];
      setPendingEvents(waiting);
      clearDraft(instance.id);
      if (!closeTab) {
        setValues(savedValues);
      }
      const nextStepIndex = res.data.nextStep ?? step + 1;
      const updatedInstanceBase = {
        ...instance,
        currentStep: nextStepIndex,
        formData: savedValues,
        currentAssignee: res.data.currentAssignee || null,
        canClaim: res.data.canClaim ?? false,
        isCurrentAssignee: res.data.isCurrentAssignee ?? false,
        isPotentialResponsible: res.data.isPotentialResponsible ?? false,
      };
      if (res.data.done) {
        alert("Processo concluído");
        if (onFinish) onFinish();
        if (!closeTab) {
          setForm(null);
          setInstance({
            ...updatedInstanceBase,
            status: "COMPLETED",
          });
        }
        if (activateTab && returnToTabId) activateTab(returnToTabId);
        if (closeTab) closeTab({ activateTabId: returnToTabId });
        return;
      }

      if (waiting.length) {
        if (!closeTab) {
          setForm(null);
          setInstance(updatedInstanceBase);
        }
        return;
      } else {
        if (!closeTab) {
          setForm(res.data.form);
          setInstance(updatedInstanceBase);
        }
      }
      if (activateTab && returnToTabId) activateTab(returnToTabId);
      if (closeTab) closeTab({ activateTabId: returnToTabId });
    } catch (err) {
      const msg = err.response?.data?.error || "Erro ao enviar dados";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const canCancelInstance =
    !!instance &&
    Number(instance.currentStep ?? 0) === 0 &&
    !["completed", "cancelled"].includes(
      String(instance.status || "").toLowerCase(),
    );

  if (loading) return <p>Carregando...</p>;
  if (error) return <p className="text-red-600">{error}</p>;
  if (cancelled)
    return <p className="text-slate-600">Solicitação cancelada com sucesso.</p>;
  if (!form) {
    if (pendingEvents.length) {
      return (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-700">Eventos pendentes</h2>
          <p className="text-slate-600">
            Esta solicitação aguarda o disparo de um evento para prosseguir. Se você recebeu uma
            mensagem ou deseja acionar manualmente um temporizador, selecione a opção desejada.
          </p>
          <ul className="space-y-3">
            {pendingEvents.map((event) => (
              <li
                key={event.id}
                className="flex flex-col gap-2 rounded border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-medium text-slate-700">{event.name || event.id}</p>
                  {event.trigger && (
                    <p className="text-sm text-slate-500">Tipo: {event.trigger}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleTriggerEvent(event)}
                  disabled={triggeringEventId === event.id}
                  className="inline-flex items-center justify-center rounded bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
                >
                  {triggeringEventId === event.id ? "Registrando..." : "Registrar evento"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return <p>Nenhum formulário para exibir.</p>;
  }

  const groups = form.schema?.groups || [];
  const fields = form.schema?.fields || [];
  const modeMap = groups.reduce(
    (acc, g) => ({ ...acc, [g.id]: g.mode || "edit" }),
    {},
  );
  const totalSteps = instance?.process?.totalSteps || 0;

  const handleCancelInstance = async () => {
    if (!instance || !canCancelInstance || cancelling) return;
    const confirmed = window.confirm(
      "Deseja cancelar esta solicitação? Os dados preenchidos serão descartados.",
    );
    if (!confirmed) return;

    setCancelling(true);
    try {
      await api.delete(`/processes/instances/${instance.id}`);
      clearDraft(instance.id);
      alert("Solicitação cancelada com sucesso.");
      if (onFinish) onFinish();
      if (closeTab) {
        closeTab({ activateTabId: returnToTabId });
      } else {
        setCancelled(true);
        setForm(null);
        setInstance(null);
        setValues({});
        setPendingEvents([]);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Não foi possível cancelar a solicitação.";
      alert(msg);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {instance && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl">
              Solicitação {instance.id} - {instance.process.name}
            </h1>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setShowHistory((prev) => !prev)}
                className={`inline-flex items-center gap-2 rounded border px-3 py-1 text-sm font-medium transition ${
                  showHistory
                    ? "border-blue-600 bg-blue-50 text-blue-600"
                    : "border-slate-300 text-slate-600 hover:bg-slate-100"
                }`}
              >
                <FaHistory className="text-xs" />
                {showHistory ? "Ocultar histórico" : "Histórico"}
              </button>
              {canCancelInstance && (
                <button
                  type="button"
                  onClick={handleCancelInstance}
                  disabled={cancelling}
                  className="rounded bg-rose-600 px-3 py-1 text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-rose-400"
                >
                  {cancelling ? "Cancelando..." : "Cancelar solicitação"}
                </button>
              )}
              <button
                onClick={handleDownloadPdf}
                className="bg-blue-600 text-white px-3 py-1 rounded"
              >
                Baixar PDF
              </button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 bg-gray-50 p-2 rounded shadow text-sm">
            <div>
              <span className="font-medium">ID:</span> {instance.id}
            </div>
            <div>
              <span className="font-medium">Processo:</span>{" "}
              {instance.process.name}
            </div>
            <div>
              <span className="font-medium">Solicitante:</span>{" "}
              {instance.requester?.name ||
                instance.user?.name ||
                instance.startedBy?.name ||
                "-"}
            </div>
            <div>
              <span className="font-medium">Início:</span>{" "}
              {new Date(instance.startedAt).toLocaleString()}
            </div>
          </div>
          {showHistory && (
            <div className="rounded border border-slate-200 bg-white p-3 shadow-sm">
              <StepHistory instanceId={instance.id} />
            </div>
          )}
          {(instance.currentAssignee?.id !== user?.id || instance.isCurrentAssignee) && (
            <div className="flex flex-col gap-2 rounded border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              {instance.currentAssignee && instance.currentAssignee.id !== user?.id && (
                <div className="text-sm text-slate-700">
                  <span className="font-semibold text-slate-800">Atribuído a:</span>{" "}
                  {instance.currentAssignee.name || `Usuário #${instance.currentAssignee.id}`}
                </div>
              )}
              {instance.isCurrentAssignee && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleReleaseTask}
                    disabled={reassigningTask}
                    className="rounded border border-slate-400 px-3 py-1 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {reassigningTask ? "Liberando..." : "Liberar"}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {!instance && <h1 className="text-2xl">{form.name}</h1>}
      {instance && (totalSteps > 0 || currentStepName) && (
        <p className="text-sm text-gray-600">
          {currentStepName ? `Etapa atual: ${currentStepName}` : "Etapa atual"}
        </p>
      )}
      {instance && mustClaimBeforeSubmit && (
        <p className="text-sm text-amber-600">
          {instance.currentAssignee && !instance.isCurrentAssignee
            ? `A tarefa está atribuída a ${
                instance.currentAssignee.name || `usuário #${instance.currentAssignee.id}`
              }. Você não pode enviar o formulário.`
            : "Assuma a tarefa antes de enviar o formulário."}
        </p>
      )}
      {groups.map((g) => {
        const gFields = fields.filter(
          (f) => f.groupId === g.id && fieldVisible(f, values),
        );
        if (gFields.length === 0) return null;
        return (
          <div key={g.id} className="mb-6 space-y-3">
            <h3 className="text-lg font-semibold text-slate-800">{g.label}</h3>
            <div className="grid grid-cols-12 gap-4">
              {gFields.map((f, idx) => {
                const groupMode = modeMap[f.groupId];
                const isGroupView = groupMode === "view";
                const dynamicReadOnly = Boolean(f.readOnly || isGroupView);
                const allowSelectionWhenReadOnly = Boolean(
                  f.lockedForStep && !isGroupView,
                );
                const columnSpan = f.size || 12;
                return (
                  <div
                    key={`${g.id}-${idx}`}
                    className="flex flex-col gap-2"
                    style={{ gridColumn: `span ${columnSpan} / span ${columnSpan}` }}
                  >
                    <div className="flex flex-col gap-1">
                      <label
                        className="text-sm font-medium text-slate-700"
                        htmlFor={f.id}
                      >
                        {f.label}
                        {f.required && <span className="text-red-600"> *</span>}
                      </label>
                      {f.description && (
                        <span className="text-xs leading-snug text-slate-500">
                          {f.description}
                        </span>
                      )}
                      {canUseAiAnswer(f, dynamicReadOnly) && (
                        <button
                          type="button"
                          onClick={() => openAiAnswerModal(f)}
                          className="mt-1 inline-flex w-fit items-center gap-2 rounded-full border border-violet-200 bg-gradient-to-r from-violet-50 via-white to-cyan-50 px-3 py-1 text-xs font-semibold text-violet-700 shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md"
                        >
                          <FaPenNib className="text-[11px]" /> Escrever com IA
                        </button>
                      )}
                    </div>
                    {renderFieldControl(f, {
                      readOnly: dynamicReadOnly,
                      allowSelectionWhenReadOnly,
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {fields.filter((f) => !f.groupId && fieldVisible(f, values)).length >
        0 && (
        <div className="mb-6 space-y-3">
          {groups.length > 0 && (
            <h3 className="text-lg font-semibold text-slate-800">Campos</h3>
          )}
          <div className="grid grid-cols-12 gap-4">
            {fields
              .filter((f) => !f.groupId && fieldVisible(f, values))
              .map((f, idx) => {
                const columnSpan = f.size || 12;
                const readOnly = Boolean(f.readOnly);
                const allowSelectionWhenReadOnly = Boolean(f.lockedForStep);
                return (
                  <div
                    key={`nogroup-run-${idx}`}
                    className="flex flex-col gap-2"
                    style={{ gridColumn: `span ${columnSpan} / span ${columnSpan}` }}
                  >
                    <div className="flex flex-col gap-1">
                      <label
                        className="text-sm font-medium text-slate-700"
                        htmlFor={f.id}
                      >
                        {f.label}
                        {f.required && <span className="text-red-600"> *</span>}
                      </label>
                      {f.description && (
                        <span className="text-xs leading-snug text-slate-500">
                          {f.description}
                        </span>
                      )}
                      {canUseAiAnswer(f, readOnly) && (
                        <button
                          type="button"
                          onClick={() => openAiAnswerModal(f)}
                          className="mt-1 inline-flex w-fit items-center gap-2 rounded-full border border-violet-200 bg-gradient-to-r from-violet-50 via-white to-cyan-50 px-3 py-1 text-xs font-semibold text-violet-700 shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md"
                        >
                          <FaPenNib className="text-[11px]" /> Escrever com IA
                        </button>
                      )}
                    </div>
                    {renderFieldControl(f, {
                      readOnly,
                      allowSelectionWhenReadOnly,
                    })}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {aiAnswerModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex min-h-screen items-stretch justify-center overflow-y-auto bg-slate-950/55 p-4 backdrop-blur-sm sm:items-center">
          <div className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-white/60 bg-white shadow-2xl">
            <div className="bg-gradient-to-br from-violet-600 via-indigo-600 to-cyan-500 p-5 text-white">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/75">NASH IA</p>
              <h2 className="mt-2 text-2xl font-bold">Responder “{aiAnswerModal.label}”</h2>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              <label className="block text-sm font-medium text-slate-700">
                Prompt complementar para a IA
                <textarea
                  value={aiAnswerPrompt}
                  onChange={(event) => setAiAnswerPrompt(event.target.value)}
                  className="mt-2 min-h-[80px] w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
                  placeholder=""
                />
              </label>
              <p className="text-sm font-medium text-slate-700">Como deseja gerar a resposta?</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setAiAnswerMode("rewrite")}
                  className={`rounded-2xl border p-4 text-left transition ${aiAnswerMode === "rewrite" ? "border-violet-400 bg-violet-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <span className="block text-sm font-semibold text-slate-800">Reescrever resposta feita</span>
                </button>
                <button
                  type="button"
                  onClick={() => setAiAnswerMode("fresh")}
                  className={`rounded-2xl border p-4 text-left transition ${aiAnswerMode === "fresh" ? "border-cyan-400 bg-cyan-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <span className="block text-sm font-semibold text-slate-800">Começar do zero</span>
                </button>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setAiAnswerModal(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
                <button type="button" onClick={handleGenerateAiAnswer} disabled={aiAnswerLoading} className="rounded-lg bg-gradient-to-r from-violet-600 to-cyan-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60">
                  {aiAnswerLoading ? "Gerando..." : "Gerar resposta"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {hasPendingUploads && (
        <div className="mt-6 rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">
          <p className="font-semibold">Aguardando envio de arquivos:</p>
          <ul className="mt-1 space-y-1">
            {pendingUploads.map((upload) => (
              <li key={upload.key}>{upload.fileName} — {upload.status === "processing" ? "Processando arquivo..." : `${upload.percent || 0}%`}</li>
            ))}
          </ul>
        </div>
      )}
      {submitError && (
        <div
          role="alert"
          aria-live="assertive"
          className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm"
        >
          <p className="font-semibold">Não foi possível enviar a solicitação.</p>
          <p className="mt-1">{submitError}</p>
        </div>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {instance?.canClaim && !instance?.isCurrentAssignee && (
          <button
            type="button"
            onClick={handleClaimTask}
            disabled={claimingTask}
            className="inline-flex items-center justify-center rounded-md border border-blue-500 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {claimingTask ? "Assumindo..." : "Assumir tarefa"}
          </button>
        )}
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSubmit}
          disabled={mustClaimBeforeSubmit || submitting || hasPendingUploads}
        >
          {hasPendingUploads ? `Aguardando ${pendingUploadsCount} arquivo(s)...` : submitting ? "Enviando..." : "Enviar"}
        </button>
      </div>
    </div>
  );
}
